import { X, Check, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { LegalModal } from "./LegalModal";
import { schedulecall } from '../../apihandlers';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type IndustryOption = {
  value: string;
  label: string;
};

const INDUSTRY_OPTIONS: IndustryOption[] = [
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance & Banking" },
  { value: "retail", label: "Retail & E-commerce" },
  { value: "technology", label: "Technology & SaaS" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "education", label: "Education" },
  { value: "logistics", label: "Logistics & Supply Chain" },
  { value: "realestate", label: "Real Estate" },
  { value: "other", label: "Other" },
];

export function ProjectModal({ isOpen, onClose }: ProjectModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Custom Dropdown State
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Legal Modal State
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!selectedIndustry) {
      const form = e.target as HTMLFormElement;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      
      if (selectedIndustry === "") return; 
    }

    setIsSubmitting(true);

    const form = e.target as HTMLFormElement;
    
    try {
      await schedulecall({
        name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
        email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
        phone: (form.elements.namedItem('phone') as HTMLInputElement)?.value,
        industry: selectedIndustry,
        message: (form.elements.namedItem('message') as HTMLTextAreaElement)?.value,
      });
      setIsSubmitting(false);
      
      setSubmitted(true);
    } catch (e: any) {
        setIsSubmitting(false);
        console.log(e);
        toast.error("Something went wrong, please try again later!");
    }

    setSelectedIndustry("");

  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setIsSubmitting(false);
      setSelectedIndustry(""); // Reset dropdown
    }, 300);
  };

  const selectedOptionLabel = INDUSTRY_OPTIONS.find(opt => opt.value === selectedIndustry)?.label;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-2xl z-[101] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Request Received!</h3>
                  <p className="text-slate-600 mb-6">
                    Thank you for sharing your project details. Our team will review them and get back to you within 24 hours.
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Book a Free Consultation</h3>
                    <p className="text-slate-600 text-sm">
                      Fill out the form below and our experts will get in touch with you to discuss your business needs.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <label htmlFor="name" className="text-sm font-semibold text-slate-700">Full Name</label>
                      <input
                        required
                        type="text"
                        name="name"
                        id="name"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750]/20 focus:border-[#FD5750] outline-none transition-all"
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label htmlFor="email" className="text-sm font-semibold text-slate-700">Email Address</label>
                        <input
                          required
                          type="email"
                          name="email"
                          id="email"
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750]/20 focus:border-[#FD5750] outline-none transition-all"
                          placeholder="john@company.com"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="phone" className="text-sm font-semibold text-slate-700">Phone Number</label>
                        <input
                          required
                          type="tel"
                          name="phone"
                          id="phone"
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750]/20 focus:border-[#FD5750] outline-none transition-all"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5 relative" ref={dropdownRef}>
                      <label className="text-sm font-semibold text-slate-700">Industry</label>
                      <div className="relative">
                        <input 
                          type="text"
                          name="industry"
                          className="absolute opacity-0 w-full h-full -z-10" 
                          value={selectedIndustry} 
                          onChange={() => {}} 
                          required 
                          onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity('Please select an industry.')}
                          onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                        />
                        
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className={`w-full px-4 py-2.5 bg-slate-50 border rounded-lg outline-none transition-all flex items-center justify-between text-left ${
                            isDropdownOpen 
                              ? "border-[#FD5750] ring-2 ring-[#FD5750]/20" 
                              : "border-slate-200 focus:border-[#FD5750] focus:ring-2 focus:ring-[#FD5750]/20"
                          }`}
                        >
                          <span className={`block truncate ${selectedIndustry ? "text-slate-900" : "text-slate-500"}`}>
                            {selectedIndustry ? selectedOptionLabel : "Select your industry"}
                          </span>
                          <motion.div
                            animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex-shrink-0"
                          >
                            <ChevronDown size={18} className="text-slate-500" />
                          </motion.div>
                        </button>

                        <AnimatePresence>
                          {isDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="absolute top-full left-0 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden max-h-60"
                            >
                              <ul className="overflow-y-auto max-h-60 py-1">
                                {INDUSTRY_OPTIONS.map((option) => (
                                  <li key={option.value}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedIndustry(option.value);
                                        setIsDropdownOpen(false);
                                      }}
                                      className={`w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors flex items-center justify-between ${
                                        selectedIndustry === option.value ? "bg-slate-50 text-[#FD5750] font-medium" : "text-slate-700"
                                      }`}
                                    >
                                      <span className="truncate">{option.label}</span>
                                      {selectedIndustry === option.value && (
                                        <Check size={16} className="text-[#FD5750] flex-shrink-0 ml-2" />
                                      )}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="description" className="text-sm font-semibold text-slate-700">How can we help you?</label>
                      <textarea
                        required
                        id="description"
                        name="message"
                        rows={4}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FD5750]/20 focus:border-[#FD5750] outline-none transition-all resize-none"
                        placeholder="Briefly describe your project or challenges..."
                      ></textarea>
                    </div>

                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="w-full py-3 mt-2 bg-[#FD5750] text-white font-bold rounded-lg hover:bg-[#E04F49] transition-all shadow-lg shadow-[#FD5750]/20 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer"
                    >
                      {isSubmitting ? (
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        "Submit Request"
                      )}
                    </button>
                    
                    <p className="text-xs text-center text-slate-400 mt-4">
                      By submitting this form, you agree to our <button type="button" onClick={() => setIsPrivacyOpen(true)} className="underline hover:text-slate-600 transition-colors">privacy policy</button>. Your information is safe with us.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <LegalModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} type="privacy" />
    </>
  );
}
